// API Service for Stock Manager with localStorage fallback
class ApiService {
  constructor() {
    this.baseURL = '/api';
    this.isMigrated = false;
    this.migrationInProgress = false;
  }

  // ================================
  // INITIALIZATION & MIGRATION
  // ================================

  async initialize() {
    if (this.migrationInProgress) {
      return;
    }

    try {
      // Check if we need to migrate localStorage data
      const hasLocalStorageData = this.hasLocalStorageData();
      const hasDatabaseData = await this.hasDatabaseData();

      if (hasLocalStorageData && !hasDatabaseData) {
        console.log('Migrating localStorage data to database...');
        await this.migrateLocalStorageToDatabase();
      }

      this.isMigrated = true;
    } catch (error) {
      console.error('Failed to initialize API service:', error);
      console.log('Falling back to localStorage mode');
      this.isMigrated = false;
    }
  }

  hasLocalStorageData() {
    const keys = [
      'supplierConfig', 'stockAtHome', 'stockInTransit', 'inventoryPrices',
      'roiPurchasePrices', 'roiSellingPricesFBM', 'roiSellingPricesFBA',
      'suppliesStock', 'fbaTransferDelay', 'supplierTransitDelay',
      'roiTimeSettings', 'roiFeeSettings', 'roiHourlyRate'
    ];

    return keys.some(key => {
      const data = localStorage.getItem(key);
      return data && data !== '{}' && data !== '[]' && data !== 'null';
    });
  }

  async hasDatabaseData() {
    try {
      const response = await fetch(`${this.baseURL}/products`);
      if (!response.ok) return false;
      
      const products = await response.json();
      return products.length > 0;
    } catch {
      return false;
    }
  }

  async migrateLocalStorageToDatabase() {
    if (this.migrationInProgress) return;
    this.migrationInProgress = true;

    try {
      // Collect all localStorage data
      const localStorageData = {
        supplierConfig: this.getFromLocalStorage('supplierConfig', {}),
        stockAtHome: this.getFromLocalStorage('stockAtHome', {}),
        stockInTransit: this.getFromLocalStorage('stockInTransit', {}),
        inventoryPrices: this.getFromLocalStorage('inventoryPrices', {}),
        roiPurchasePrices: this.getFromLocalStorage('roiPurchasePrices', {}),
        roiSellingPricesFBM: this.getFromLocalStorage('roiSellingPricesFBM', {}),
        roiSellingPricesFBA: this.getFromLocalStorage('roiSellingPricesFBA', {}),
        suppliesStock: this.getFromLocalStorage('suppliesStock', []),
        fbaTransferDelay: localStorage.getItem('fbaTransferDelay') || '10',
        supplierTransitDelay: localStorage.getItem('supplierTransitDelay') || '30',
        roiTimeSettings: this.getFromLocalStorage('roiTimeSettings', {}),
        roiFeeSettings: this.getFromLocalStorage('roiFeeSettings', {}),
        roiHourlyRate: localStorage.getItem('roiHourlyRate') || '25'
      };

      // Send migration request to backend
      const response = await fetch(`${this.baseURL}/migrate/localStorage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(localStorageData)
      });

      if (!response.ok) {
        throw new Error('Migration failed');
      }

      const result = await response.json();
      console.log('Migration successful:', result);

      // Create backup of localStorage data before clearing
      this.createBackup(localStorageData);

      // Clear localStorage after successful migration
      this.clearMigratedLocalStorage();

      // Set migration flag
      localStorage.setItem('migrated_to_database', 'true');

      this.isMigrated = true;
    } catch (error) {
      console.error('Migration failed:', error);
      throw error;
    } finally {
      this.migrationInProgress = false;
    }
  }

  createBackup(data) {
    const backup = {
      ...data,
      backupDate: new Date().toISOString(),
      version: '1.0'
    };

    const jsonString = JSON.stringify(backup, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    const date = new Date().toISOString().slice(0, 10);
    const time = new Date().toISOString().slice(11, 19).replace(/:/g, '-');
    link.href = url;
    link.setAttribute('download', `stock-manager-migration-backup-${date}_${time}.json`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('Backup created successfully');
  }

  clearMigratedLocalStorage() {
    const keysToKeep = ['migrated_to_database'];
    const keysToClear = [
      'supplierConfig', 'stockAtHome', 'stockInTransit', 'inventoryPrices',
      'roiPurchasePrices', 'roiSellingPricesFBM', 'roiSellingPricesFBA',
      'suppliesStock', 'fbaTransferDelay', 'supplierTransitDelay',
      'roiTimeSettings', 'roiFeeSettings', 'roiHourlyRate'
    ];

    keysToClear.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  getFromLocalStorage(key, defaultValue) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  // ================================
  // GENERIC API METHODS
  // ================================

  async apiRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get(endpoint) {
    if (!this.isMigrated) {
      throw new Error('API not available, using localStorage fallback');
    }
    return this.apiRequest(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    if (!this.isMigrated) {
      throw new Error('API not available, using localStorage fallback');
    }
    return this.apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    if (!this.isMigrated) {
      throw new Error('API not available, using localStorage fallback');
    }
    return this.apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    if (!this.isMigrated) {
      throw new Error('API not available, using localStorage fallback');
    }
    return this.apiRequest(endpoint, { method: 'DELETE' });
  }

  // ================================
  // SUPPLIERS API
  // ================================

  async getSuppliers() {
    try {
      return await this.get('/suppliers');
    } catch (error) {
      console.log('Using localStorage fallback for suppliers');
      const config = this.getFromLocalStorage('supplierConfig', {});
      return config.suppliers || [];
    }
  }

  async createSupplier(supplierData) {
    try {
      return await this.post('/suppliers', supplierData);
    } catch (error) {
      console.log('Using localStorage fallback for creating supplier');
      const config = this.getFromLocalStorage('supplierConfig', { suppliers: [], productSuppliers: {} });
      const newId = Math.max(0, ...config.suppliers.map(s => s.id)) + 1;
      const newSupplier = { id: newId, ...supplierData };
      
      config.suppliers.push(newSupplier);
      localStorage.setItem('supplierConfig', JSON.stringify(config));
      return newSupplier;
    }
  }

  async updateSupplier(id, supplierData) {
    try {
      return await this.put(`/suppliers/${id}`, supplierData);
    } catch (error) {
      console.log('Using localStorage fallback for updating supplier');
      const config = this.getFromLocalStorage('supplierConfig', { suppliers: [], productSuppliers: {} });
      const supplierIndex = config.suppliers.findIndex(s => s.id === parseInt(id));
      
      if (supplierIndex >= 0) {
        config.suppliers[supplierIndex] = { id: parseInt(id), ...supplierData };
        localStorage.setItem('supplierConfig', JSON.stringify(config));
        return config.suppliers[supplierIndex];
      }
      throw new Error('Supplier not found');
    }
  }

  async deleteSupplier(id) {
    try {
      return await this.delete(`/suppliers/${id}`);
    } catch (error) {
      console.log('Using localStorage fallback for deleting supplier');
      const config = this.getFromLocalStorage('supplierConfig', { suppliers: [], productSuppliers: {} });
      config.suppliers = config.suppliers.filter(s => s.id !== parseInt(id));
      localStorage.setItem('supplierConfig', JSON.stringify(config));
      return { success: true };
    }
  }

  // ================================
  // PRODUCTS API
  // ================================

  async getProducts() {
    try {
      return await this.get('/products');
    } catch (error) {
      console.log('Using localStorage fallback for products');
      // Reconstruct products from localStorage
      const stockAtHome = this.getFromLocalStorage('stockAtHome', {});
      const stockInTransit = this.getFromLocalStorage('stockInTransit', {});
      const config = this.getFromLocalStorage('supplierConfig', {});
      
      const allSkus = new Set([
        ...Object.keys(stockAtHome),
        ...Object.keys(stockInTransit)
      ]);

      return Array.from(allSkus).map(sku => ({
        sku,
        supplier_id: config.productSuppliers?.[sku] || null,
        home_quantity: stockAtHome[sku] || 0,
        transit_quantity: typeof stockInTransit[sku] === 'object' 
          ? stockInTransit[sku].quantity || 0 
          : stockInTransit[sku] || 0,
        transit_order_date: typeof stockInTransit[sku] === 'object' 
          ? stockInTransit[sku].orderDate 
          : null,
        transit_supplier_id: typeof stockInTransit[sku] === 'object' 
          ? stockInTransit[sku].supplierId 
          : null,
        prices: this.getProductPrices(sku)
      }));
    }
  }

  getProductPrices(sku) {
    const prices = {};
    const priceTypes = {
      inventoryPrices: 'inventory',
      roiPurchasePrices: 'purchase',
      roiSellingPricesFBM: 'selling_fbm',
      roiSellingPricesFBA: 'selling_fba'
    };

    for (const [storageKey, priceType] of Object.entries(priceTypes)) {
      const priceData = this.getFromLocalStorage(storageKey, {});
      if (priceData[sku]) {
        prices[priceType] = priceData[sku];
      }
    }

    return prices;
  }

  // ================================
  // STOCK API
  // ================================

  async updateHomeStock(sku, quantity) {
    try {
      return await this.put(`/stock/home/${encodeURIComponent(sku)}`, { quantity });
    } catch (error) {
      console.log('Using localStorage fallback for home stock');
      const stockAtHome = this.getFromLocalStorage('stockAtHome', {});
      stockAtHome[sku] = quantity;
      localStorage.setItem('stockAtHome', JSON.stringify(stockAtHome));
      return { sku, quantity, type: 'home' };
    }
  }

  async updateFcTransfer(sku, fc_transfer) {
    try {
      return await this.put(`/products/${encodeURIComponent(sku)}/fc_transfer`, { fc_transfer });
    } catch (error) {
      console.log('Using localStorage fallback for FC Transfer');
      const fcTransfers = this.getFromLocalStorage('fcTransfers', {});
      fcTransfers[sku] = fc_transfer;
      localStorage.setItem('fcTransfers', JSON.stringify(fcTransfers));
      return { sku, fc_transfer };
    }
  }

  async updateTransitStock(sku, quantity, orderDate, supplierId) {
    try {
      return await this.put(`/stock/transit/${encodeURIComponent(sku)}`, {
        quantity,
        order_date: orderDate,
        supplier_id: supplierId
      });
    } catch (error) {
      console.log('Using localStorage fallback for transit stock');
      const stockInTransit = this.getFromLocalStorage('stockInTransit', {});
      
      if (quantity > 0) {
        stockInTransit[sku] = {
          quantity,
          orderDate,
          supplierId
        };
      } else {
        delete stockInTransit[sku];
      }
      
      localStorage.setItem('stockInTransit', JSON.stringify(stockInTransit));
      return { sku, quantity, order_date: orderDate, supplier_id: supplierId, type: 'transit' };
    }
  }

  async transferTransitToHome(sku) {
    try {
      return await this.post(`/stock/transfer/${encodeURIComponent(sku)}`);
    } catch (error) {
      console.log('Using localStorage fallback for stock transfer');
      const stockAtHome = this.getFromLocalStorage('stockAtHome', {});
      const stockInTransit = this.getFromLocalStorage('stockInTransit', {});
      
      const transitInfo = stockInTransit[sku];
      if (!transitInfo) {
        throw new Error('No transit stock to transfer');
      }
      
      const transitQuantity = typeof transitInfo === 'object' 
        ? transitInfo.quantity || 0 
        : transitInfo || 0;
      
      if (transitQuantity <= 0) {
        throw new Error('No transit stock to transfer');
      }
      
      const currentHome = stockAtHome[sku] || 0;
      const newHomeQuantity = currentHome + transitQuantity;
      
      stockAtHome[sku] = newHomeQuantity;
      delete stockInTransit[sku];
      
      localStorage.setItem('stockAtHome', JSON.stringify(stockAtHome));
      localStorage.setItem('stockInTransit', JSON.stringify(stockInTransit));
      
      return {
        sku,
        transferred_quantity: transitQuantity,
        new_home_quantity: newHomeQuantity
      };
    }
  }

  // ================================
  // PRICES API
  // ================================

  async updatePrice(sku, priceType, price) {
    try {
      return await this.put(`/prices/${encodeURIComponent(sku)}/${priceType}`, { price });
    } catch (error) {
      console.log('Using localStorage fallback for price update');
      const storageKeys = {
        inventory: 'inventoryPrices',
        purchase: 'roiPurchasePrices',
        selling_fbm: 'roiSellingPricesFBM',
        selling_fba: 'roiSellingPricesFBA'
      };
      
      const storageKey = storageKeys[priceType];
      if (!storageKey) {
        throw new Error('Invalid price type');
      }
      
      const prices = this.getFromLocalStorage(storageKey, {});
      prices[sku] = price;
      localStorage.setItem(storageKey, JSON.stringify(prices));
      
      return { sku, price_type: priceType, price };
    }
  }

  // ================================
  // SUPPLIES API
  // ================================

  async getSupplies() {
    try {
      return await this.get('/supplies');
    } catch (error) {
      console.log('Using localStorage fallback for supplies');
      return this.getFromLocalStorage('suppliesStock', []);
    }
  }

  async createSupply(supplyData) {
    try {
      return await this.post('/supplies', supplyData);
    } catch (error) {
      console.log('Using localStorage fallback for creating supply');
      const supplies = this.getFromLocalStorage('suppliesStock', []);
      const newId = Date.now();
      const newSupply = { id: newId, ...supplyData };
      
      supplies.push(newSupply);
      localStorage.setItem('suppliesStock', JSON.stringify(supplies));
      return newSupply;
    }
  }

  async updateSupply(id, supplyData) {
    try {
      return await this.put(`/supplies/${id}`, supplyData);
    } catch (error) {
      console.log('Using localStorage fallback for updating supply');
      const supplies = this.getFromLocalStorage('suppliesStock', []);
      const supplyIndex = supplies.findIndex(s => s.id === parseInt(id));
      
      if (supplyIndex >= 0) {
        supplies[supplyIndex] = { id: parseInt(id), ...supplyData };
        localStorage.setItem('suppliesStock', JSON.stringify(supplies));
        return supplies[supplyIndex];
      }
      throw new Error('Supply not found');
    }
  }

  async deleteSupply(id) {
    try {
      return await this.delete(`/supplies/${id}`);
    } catch (error) {
      console.log('Using localStorage fallback for deleting supply');
      const supplies = this.getFromLocalStorage('suppliesStock', []);
      const updatedSupplies = supplies.filter(s => s.id !== parseInt(id));
      localStorage.setItem('suppliesStock', JSON.stringify(updatedSupplies));
      return { success: true };
    }
  }

  // ================================
  // SETTINGS API
  // ================================

  async getSettings() {
    try {
      return await this.get('/settings');
    } catch (error) {
      console.log('Using localStorage fallback for settings');
      return {
        fbaTransferDelay: localStorage.getItem('fbaTransferDelay') || '10',
        supplierTransitDelay: localStorage.getItem('supplierTransitDelay') || '30',
        roiHourlyRate: localStorage.getItem('roiHourlyRate') || '25'
      };
    }
  }

  async updateSetting(key, value) {
    try {
      return await this.put(`/settings/${key}`, { value });
    } catch (error) {
      console.log('Using localStorage fallback for setting update');
      localStorage.setItem(key, String(value));
      return { key, value };
    }
  }

  // ================================
  // ROI SETTINGS API
  // ================================

  async getRoiSettings() {
    try {
      return await this.get('/roi/settings');
    } catch (error) {
      console.log('Using localStorage fallback for ROI settings');
      return {
        time: this.getFromLocalStorage('roiTimeSettings', {}),
        fee: this.getFromLocalStorage('roiFeeSettings', {})
      };
    }
  }

  async updateRoiSettings(settings) {
    try {
      return await this.put('/roi/settings', settings);
    } catch (error) {
      console.log('Using localStorage fallback for ROI settings update');
      
      if (settings.time) {
        localStorage.setItem('roiTimeSettings', JSON.stringify(settings.time));
      }
      
      if (settings.fee) {
        localStorage.setItem('roiFeeSettings', JSON.stringify(settings.fee));
      }
      
      return { success: true };
    }
  }

  // ================================
  // EXPORT API
  // ================================

  async exportData() {
    try {
      return await this.get('/export');
    } catch (error) {
      console.log('Using localStorage fallback for export');
      // Create export from localStorage
      return {
        supplierConfig: this.getFromLocalStorage('supplierConfig', {}),
        stockAtHome: this.getFromLocalStorage('stockAtHome', {}),
        stockInTransit: this.getFromLocalStorage('stockInTransit', {}),
        inventoryPrices: this.getFromLocalStorage('inventoryPrices', {}),
        roiPurchasePrices: this.getFromLocalStorage('roiPurchasePrices', {}),
        roiSellingPricesFBM: this.getFromLocalStorage('roiSellingPricesFBM', {}),
        roiSellingPricesFBA: this.getFromLocalStorage('roiSellingPricesFBA', {}),
        suppliesStock: this.getFromLocalStorage('suppliesStock', []),
        fbaTransferDelay: localStorage.getItem('fbaTransferDelay') || '10',
        supplierTransitDelay: localStorage.getItem('supplierTransitDelay') || '30',
        roiTimeSettings: this.getFromLocalStorage('roiTimeSettings', {}),
        roiFeeSettings: this.getFromLocalStorage('roiFeeSettings', {}),
        roiHourlyRate: localStorage.getItem('roiHourlyRate') || '25',
        exportDate: new Date().toISOString(),
        appVersion: '1.0'
      };
    }
  }

  // ================================
  // AMAZON IMPORT STATUS & MANAGEMENT
  // ================================

  async getImportStatus() {
    try {
      const response = await this.get('/imports/status');
      return response;
    } catch (error) {
      console.log('Using localStorage fallback for import status');
      // Fallback vers localStorage
      const lastImport = localStorage.getItem('lastAmazonImport');
      const now = new Date();
      
      if (!lastImport) {
        return {
          lastImport: null,
          canImport: true,
          nextImportIn: null
        };
      }

      const lastImportTime = new Date(lastImport);
      const timeSinceLastImport = now - lastImportTime;
      const fourHoursInMs = 4 * 60 * 60 * 1000; // 4 heures en millisecondes
      
      if (timeSinceLastImport >= fourHoursInMs) {
        return {
          lastImport: lastImport,
          canImport: true,
          nextImportIn: null
        };
      } else {
        const timeUntilNext = fourHoursInMs - timeSinceLastImport;
        const minutesUntilNext = Math.ceil(timeUntilNext / (1000 * 60));
        
        return {
          lastImport: lastImport,
          canImport: false,
          nextImportIn: minutesUntilNext
        };
      }
    }
  }

  async refreshData() {
    try {
      const response = await this.get('/refresh');
      return response;
    } catch (error) {
      console.log('Refresh data fallback');
      // Pour le fallback localStorage, on simule juste un succès
      return { success: true, message: 'Données actualisées depuis localStorage' };
    }
  }

  async realAmazonImport() {
    try {
      const response = await this.post('/imports/amazon');
      return response;
    } catch (error) {
      console.log('Using localStorage fallback for real import');
      // Fallback en localStorage
      const now = new Date().toISOString();
      localStorage.setItem('lastAmazonImport', now);
      
      return {
        success: false,
        error: 'Import Amazon échoué - mode fallback',
        message: error.message || 'Erreur de connexion',
        timestamp: now,
        productsImported: 0
      };
    }
  }

  async testAmazonConnection() {
    try {
      const response = await this.get('/imports/test');
      return response;
    } catch (error) {
      console.log('Test connection failed');
      return {
        success: false,
        error: 'Test de connexion échoué',
        message: error.message || 'Erreur de connexion'
      };
    }
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;